import React, { useState } from 'react';
import { Card, Table, Typography, Tag, Space, Button, Modal } from 'antd';
import { CheckCircleOutlined, WarningOutlined, EyeOutlined } from '@ant-design/icons';
import { formatINR } from '../../../shared/utils/formatters';
import type { BillingPeriodResponse } from '../types';

const { Text } = Typography;

interface Props {
    billingData?: BillingPeriodResponse;
    isLoading: boolean;
    page: number;
    setPage: (page: number) => void;
    statusFilter: string | null;
    setStatusFilter: (status: string | null) => void;
}

export const BillingAuditTable: React.FC<Props> = ({ 
    billingData, 
    isLoading, 
    page, 
    setPage, 
    statusFilter, 
    setStatusFilter 
}) => {
    const [selectedSnapshot, setSelectedSnapshot] = useState<any>(null);
    const [isSnapshotModalVisible, setIsSnapshotModalVisible] = useState(false);

    const columns = [
        {
            title: 'Merchant ID',
            dataIndex: 'merchant_id',
            key: 'merchant_id',
            render: (id: number) => <Text strong>#{id}</Text>
        },
        {
            title: 'Period',
            dataIndex: 'billing_period',
            key: 'billing_period',
        },
        {
            title: 'Type',
            dataIndex: 'billing_type',
            key: 'billing_type',
            render: (type: string) => {
                return type === 'grace_proration' ? <Tag color="orange">Grace Proration</Tag> : <Tag color="blue">Renewal</Tag>;
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                let color = 'default';
                if (status === 'paid') color = 'green';
                if (status === 'failed') color = 'red';
                if (status === 'grace') color = 'orange';
                return <Tag color={color}>{status.toUpperCase()}</Tag>;
            }
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number) => formatINR(amount / 100)
        },
        {
            title: '3-Way Match',
            key: 'reconciled',
            render: (_: any, record: any) => {
                if (record.status !== 'paid') return <Text type="secondary">-</Text>;
                if (record.reconciled) return <Tag icon={<CheckCircleOutlined />} color="success">Reconciled</Tag>;
                return <Tag icon={<WarningOutlined />} color="error">Mismatch</Tag>;
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: any) => (
                <Button 
                    type="link" 
                    icon={<EyeOutlined />} 
                    onClick={() => {
                        setSelectedSnapshot(record.snapshot);
                        setIsSnapshotModalVisible(true);
                    }}
                >
                    Snapshot
                </Button>
            )
        }
    ];

    return (
        <>
            <Card title="Billing & Audit Trail" extra={
                <Space>
                    <Button onClick={() => setStatusFilter(null)} type={statusFilter === null ? 'primary' : 'default'}>All</Button>
                    <Button onClick={() => setStatusFilter('paid')} type={statusFilter === 'paid' ? 'primary' : 'default'}>Paid</Button>
                    <Button onClick={() => setStatusFilter('grace')} type={statusFilter === 'grace' ? 'primary' : 'default'}>Grace</Button>
                    <Button onClick={() => setStatusFilter('failed')} type={statusFilter === 'failed' ? 'primary' : 'default'}>Failed</Button>
                </Space>
            }>
                <Table 
                    columns={columns} 
                    dataSource={billingData?.data || []}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{
                        current: billingData?.current_page || 1,
                        pageSize: billingData?.per_page || 20,
                        total: billingData?.total || 0,
                        onChange: (p) => setPage(p)
                    }}
                />
            </Card>

            <Modal
                title="Immutable Calculation Snapshot"
                open={isSnapshotModalVisible}
                onCancel={() => setIsSnapshotModalVisible(false)}
                footer={null}
                width={700}
            >
                <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, overflowX: 'auto' }}>
                    {JSON.stringify(selectedSnapshot, null, 2)}
                </pre>
            </Modal>
        </>
    );
};
